// =============================================================================
// features/relatorios/hooks/useReportsPeople.ts — TanStack Query para
// GET /api/reports/people (alimenta o filtro "por pessoa").
//
// - staleTime: 10 min. Quadro de pessoal muda muito menos que métrica.
// - 403 (papel sem dashboard:read, ex.: agente) devolve lista vazia em vez de
//   erro: o filtro simplesmente não aparece para quem não pode usá-lo.
// =============================================================================

import type { ReportPerson } from '@elemento/shared-schemas';
import { useQuery } from '@tanstack/react-query';

import { ApiError } from '../../../lib/api';
import { fetchReportsPeople } from '../api';

interface UseReportsPeopleResult {
  people: ReportPerson[];
  isLoading: boolean;
}

export function useReportsPeople(enabled: boolean): UseReportsPeopleResult {
  const { data, isLoading } = useQuery<ReportPerson[], Error>({
    queryKey: ['reports', 'people'],
    queryFn: async () => {
      try {
        const res = await fetchReportsPeople();
        return res.people;
      } catch (err: unknown) {
        if (err instanceof ApiError && err.status === 403) return [];
        throw err;
      }
    },
    enabled,
    staleTime: 10 * 60_000,
    retry: (failureCount, err) => {
      if (err instanceof ApiError && (err.status === 403 || err.status === 404)) return false;
      return failureCount < 2;
    },
  });

  return { people: data ?? [], isLoading };
}
